import { AppPageHeader, InfiniteTable } from '@/components/_common';
import CpfCnpjInput from '@/components/_common/CpfCnpjInput';
import DeleteIcon from '@/components/_common/DeleteIcon';
import EditIcon from '@/components/_common/EditIcon';
import {
  useInvoiceCarriers,
  useRemoveInvoiceCarrier,
} from '@/core/domain/invoice/invoiceCarrier/invoiceCarrier.hooks';
import {
  InvoiceCarrier,
  InvoiceCarrierListRequest,
} from '@/core/domain/invoice/invoiceCarrier/invoiceCarrier.types';
import { useDebounce } from '@/hooks';
import { applyCpfCnpjMask } from '@/utils/mask';
import { removeCpfCnpjMask } from '@/utils/mask/removeMask';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { Col, Divider, Modal, Row, Space } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useState } from 'react';
import { useIntl } from 'react-intl';

interface Props {
  handleModalOpen: (id: number) => void;
}

const TransportList = ({ handleModalOpen }: Props) => {
  const intl = useIntl();

  const [queryParams, setQueryParams] = useState<InvoiceCarrierListRequest>({
    ...convertParams<InvoiceCarrierListRequest>({
      cpfCnpj: 'string',
      name: 'string',
    }),
  });

  usePersistSearchParams(queryParams);
  const queryDebounce = useDebounce(queryParams);

  const carrierList = useInvoiceCarriers({
    ...queryDebounce,
    cpfCnpj: queryDebounce?.cpfCnpj
      ? removeCpfCnpjMask(queryDebounce.cpfCnpj)
      : undefined,
  });

  const carries = flatInfiniteData(carrierList.data);

  const removeMutation = useRemoveInvoiceCarrier();

  const handleRemoveClick = (transportId: number) => {
    Modal.confirm({
      title: 'Excluir transportadora',
      content: 'Deseja realmente excluir a transportadora',
      onOk: async () => {
        try {
          await removeMutation.mutateAsync(transportId);
        } catch {}
      },
      okText: intl.formatMessage({
        id: 'insurers.remove.modal.button.ok',
      }),
      okType: 'danger',
    });
  };

  const columns: ColumnsType<InvoiceCarrier> = [
    {
      dataIndex: 'name',
      title: 'Nome',
      ellipsis: true,
      width: 120,
    },
    {
      dataIndex: 'cpfCnpj',
      title: 'CPF/CNPJ',
      ellipsis: true,
      width: 120,
      render: (value) => applyCpfCnpjMask(value) || '-',
    },
    {
      title: 'Ações',
      align: 'right',
      fixed: 'right',
      width: 120,
      render: (row) => (
        <Space>
          <EditIcon
            iconProps={{
              onClick: () => handleModalOpen(row.id),
            }}
          />

          <Divider type="vertical" />

          <DeleteIcon onClick={() => handleRemoveClick(row.id)} />
        </Space>
      ),
    },
  ];

  return (
    <>
      <AppPageHeader
        searchValue={queryParams?.name}
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, name: value }))
        }
        extraFilters={
          <Row>
            <Col span={24} sm={12}>
              <CpfCnpjInput
                placeholder="CPF/CNPJ"
                value={queryParams.cpfCnpj}
                onChange={(value) =>
                  setQueryParams((prev) => ({ ...prev, cpfCnpj: value }))
                }
              />
            </Col>
          </Row>
        }
      />

      <InfiniteTable
        columns={columns}
        dataSource={carries}
        loading={carrierList.isLoading}
        hasNextPage={carrierList.hasNextPage}
        fetchNextPage={carrierList.fetchNextPage}
        isFetching={carrierList.isFetching}
        rowKey={(item) => item.id}
      />
    </>
  );
};

export default TransportList;
